import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, catchError, filter, of, shareReplay, switchMap } from 'rxjs';
import { QuoterStateService, DriverDataDraft } from './quoter-state.service';
import { InactivityService } from '../../../core/infrastructure/services/inactivity/inactivity.service';
import { StorageService } from '../../../core/infrastructure/services/storage/storage.service';
import { ReportLeadExceptionUseCase } from '../../../core/application/use-cases/lead/report-lead-exception.use-case';
import { SaveLeadPauseDataUseCase } from '../../../core/application/use-cases/lead/save-lead-pause-data.use-case';
import { UpdateOwnerPayerUseCase } from '../../../core/application/use-cases/vehicle-lookup/update-owner-payer.use-case';
import { UpdateOwnerPayerInput } from '../../../core/domain/interfaces/vehicle-lookup/vehicle-lookup.interface';
import { esCelularColombiaValido } from '../../../shared/validators/custom-validators';
import { environment } from '../../../../environments/environment';
import { PendingRescueException } from '../pages/rescue/rescue.component';

export const INACTIVITY_ROUTE = '/cotizador/inactividad-planes';

const ORIGIN_STORAGE_KEY = 'quoter_inactivity_origin';
const ABANDON_CONFIRMED_STORAGE_KEY = 'quoter_inactivity_confirmed';

export interface InactivityScreen {
  path: string;
  inactivityRoute: string;
  requiredStatus: string;
  percentageLead: number;
  sendsFilledData: boolean;
}

export const INACTIVITY_SCREENS: InactivityScreen[] = [
  {
    path: '/cotizador/planes',
    inactivityRoute: INACTIVITY_ROUTE,
    requiredStatus: 'Inactividad planes',
    percentageLead: 60,
    sendsFilledData: false,
  },
  {
    path: '/cotizador/datos-conductor',
    inactivityRoute: '/cotizador/inactividad-tomador',
    requiredStatus: 'Inactividad datos dueño',
    percentageLead: 70,
    sendsFilledData: true,
  },
  {
    path: '/cotizador/resumen',
    inactivityRoute: '/cotizador/inactividad-resumen',
    requiredStatus: 'Inactividad resumen compra',
    percentageLead: 95,
    sendsFilledData: false,
  },
];

@Injectable({ providedIn: 'root' })
export class InactivityFlowService {
  private readonly router = inject(Router);
  private readonly inactivity = inject(InactivityService);
  private readonly quoterState = inject(QuoterStateService);
  private readonly storage = inject(StorageService);
  private readonly reportLeadExceptionUseCase = inject(ReportLeadExceptionUseCase);
  private readonly saveLeadPauseDataUseCase = inject(SaveLeadPauseDataUseCase);
  private readonly updateOwnerPayerUseCase = inject(UpdateOwnerPayerUseCase);

  private initialized = false;
  private abandonReported = false;

  private readonly _abandonConfirmed = signal(false);
  readonly abandonConfirmed = this._abandonConfirmed.asReadonly();

  readonly hasContactData = computed(() => this.hasValidContactData(this.resolveDraft()));

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.watch(event.urlAfterRedirects));
  }

  reportAbandonFromInactivityScreen(): Observable<unknown> {
    return this.reportAbandonOnce();
  }


  pauseWatching(): void {
    this.inactivity.stop();
  }

  resumeWatching(): void {
    this.watch(this.router.url);
  }

  /** "Seguir en línea": el usuario volvió, NO se actualiza el lead (acordado con negocio). */
  continueOnline(): void {
    this.abandonReported = false;
    this._abandonConfirmed.set(false);
    this.storage.removeSession(ORIGIN_STORAGE_KEY);
    this.storage.removeSession(ABANDON_CONFIRMED_STORAGE_KEY);
    this.inactivity.stop();
  }

  private watch(url: string): void {
    const timeoutMs = environment.inactivityTimeoutMinutes * 60_000;

    if (INACTIVITY_SCREENS.some((screen) => url.startsWith(screen.inactivityRoute))) {
      if (this.originScreen()) {
        if (this.storage.getSession<boolean>(ABANDON_CONFIRMED_STORAGE_KEY)) {
          this.abandonReported = true;
          this._abandonConfirmed.set(true);
          this.inactivity.stop();
          return;
        }

        this.inactivity.start(timeoutMs, () => this.reportAbandonOnce());
        return;
      }

      this.inactivity.start(timeoutMs, () => this.reportAbandonOnce());
      return;
    }

    this.abandonReported = false;
    this._abandonConfirmed.set(false);
    this.storage.removeSession(ORIGIN_STORAGE_KEY);
    this.storage.removeSession(ABANDON_CONFIRMED_STORAGE_KEY);

    const screen = INACTIVITY_SCREENS.find((candidate) => url.startsWith(candidate.path));
    if (!screen) {
      this.inactivity.stop();
      return;
    }

    this.inactivity.start(timeoutMs, () => this.onInactive(screen));
  }
  private onInactive(screen: InactivityScreen): void {
    this.storage.setSession(ORIGIN_STORAGE_KEY, screen.path);
    void this.router.navigate([screen.inactivityRoute]);
  }

  private reportAbandonOnce(): Observable<unknown> {
    if (this.abandonReported) return of(undefined);

    const screen = this.originScreen();
    if (!screen) {
      console.warn('[inactividad] no hay pantalla de origen registrada, se omite el reporte.');
      return of(undefined);
    }

    this.abandonReported = true;
    this._abandonConfirmed.set(true);
    this.storage.setSession(ABANDON_CONFIRMED_STORAGE_KEY, true);
    this.inactivity.stop();
    const report$ = this.report(screen).pipe(shareReplay(1));
    report$.subscribe();
    return report$;
  }

  /** Reconstruye la pantalla origen desde sessionStorage: sobrevive a un refresh en "¿Sigues ahí?". */
  private originScreen(): InactivityScreen | null {
    const path = this.storage.getSession<string>(ORIGIN_STORAGE_KEY);
    return INACTIVITY_SCREENS.find((screen) => screen.path === path) ?? null;
  }

  pendingRescueException(): PendingRescueException | null {
    const screen = this.originScreen();
    if (!screen) return null;

    return { requiredStatus: screen.requiredStatus, percentageLead: screen.percentageLead };
  }

  private report(screen: InactivityScreen): Observable<unknown> {
    const leadId = this.quoterState.state().leadId;
    if (!leadId) {
      console.warn('[inactividad] no hay leadId en el estado, se omite la actualización.');
      return of(undefined);
    }

    const draft = this.resolveDraft();

    return this.sendFilledData(screen, leadId, draft).pipe(
      switchMap(() =>
        this.reportLeadExceptionUseCase.execute({
          leadId,
          requiredStatus: screen.requiredStatus,
          percentageLead: screen.percentageLead,
          estadoRescate: this.canBeRescued(draft) ? 'Habilitado' : 'Inhabilitado',
        }),
      ),
      catchError((err: unknown) => {
        console.error('[inactividad] error al actualizar el lead:', err);
        return of(undefined);
      }),
    );
  }

  private sendFilledData(
    screen: InactivityScreen,
    leadId: string,
    draft: DriverDataDraft | null,
  ): Observable<unknown> {
    if (!screen.sendsFilledData) return of(undefined);

    const pendingInput = this.quoterState.state()
      .pendingOwnerPayerInput as UpdateOwnerPayerInput | null;
    if (pendingInput) {
      return this.updateOwnerPayerUseCase.execute(pendingInput).pipe(
        catchError((err: unknown) => {
          console.error('[inactividad] error al enviar los datos del dueño/tomador:', err);
          return of(undefined);
        }),
      );
    }

    if (!draft?.dataTreatment || (!draft.phone && !draft.email)) return of(undefined);

    return this.saveLeadPauseDataUseCase
      .execute({
        leadId,
        phone: draft.phone ?? '',
        email: draft.email ?? '',
        acceptTos: true,
        timestamp: new Date().toISOString(),
      })
      .pipe(
        catchError((err: unknown) => {
          console.error('[inactividad] error al guardar los datos de contacto:', err);
          return of(undefined);
        }),
      );
  }

  private resolveDraft(): DriverDataDraft | null {
    const { driverDataDraft, driverData } = this.quoterState.state();
    if (driverDataDraft) return driverDataDraft;

    const submitted = driverData as DriverDataDraft | null;
    return submitted ?? null;
  }

  private canBeRescued(draft: DriverDataDraft | null): boolean {
    return !!draft?.dataTreatment && esCelularColombiaValido(draft.phone);
  }

  private hasValidContactData(draft: DriverDataDraft | null): boolean {
    return !!draft?.dataTreatment && esCelularColombiaValido(draft.phone) && !!draft?.email;
  }
}
